import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import StatisticsPage, { personPath } from '../../components/statistics/StatisticsPage';
import DataTable from '../../components/statistics/DataTable';
import { useStatisticsData, useStatisticsFilters } from '../../components/statistics/useStatistics';
import { getTrust } from '../../services/statisticsService';
import { getReports, getUnreadCount } from '../../services/reportService';
import { formatCount, formatDateTime, formatShare } from '../../utils/period';

const Person = ({ person }: { person: { userId: number; fullName: string | null } }) => (
  <Link to={personPath(person.userId)}>{person.fullName || `#${person.userId}`}</Link>
);

/** The newest unread support reports, with the count, linking to the inbox. */
const InboxSummary = () => {
  const [items, setItems] = useState<any[] | null>(null);
  const [unread, setUnread] = useState<number | null>(null);

  useEffect(() => {
    getReports({ status: 'unread', page: 1, limit: 5 })
      .then((res: any) => setItems(res?.data?.items || []))
      .catch(() => setItems([]));
    getUnreadCount()
      .then((res: any) => setUnread(res?.data?.count ?? null))
      .catch(() => setUnread(null));
  }, []);

  if (items === null) return <p className="stats-loading">Loading…</p>;
  return (
    <>
      <p className="stats-section-intro">
        {formatCount(unread)} unread. <Link to="/reports">Open the inbox</Link>
      </p>
      {items.length === 0 ? (
        <p className="stats-empty">Nothing unread.</p>
      ) : (
        <div className="stats-table-scroll">
          <table className="stats-table">
            <thead>
              <tr>
                <th><button type="button">From</button></th>
                <th><button type="button">Subject</button></th>
                <th><button type="button">Received</button></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id}>
                  <td>{r.reporter ? <Person person={{ userId: r.reporter.id, fullName: r.reporter.fullName }} /> : '—'}</td>
                  <td className="stats-comment">{r.subject}</td>
                  <td>{formatDateTime(r.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

/** PRD §5.5 — the accounts worth a look before they generate a complaint. */
const TrustSafety = () => {
  const [filters, setFilters] = useStatisticsFilters();
  const { data, loading, error, reload } = useStatisticsData(getTrust, filters);
  const cancellations = data?.cancellations;

  return (
    <StatisticsPage
      title="Trust & Safety"
      subtitle="Low ratings, members several people have blocked, and drivers who cancel unusually often."
      filters={filters}
      onFiltersChange={setFilters}
      period={data?.period}
      loading={loading}
      error={error}
      onRetry={reload}
      showComparison={false}
    >
      <section className="stats-section">
        <h2>Low ratings</h2>
        <p className="stats-section-intro">
          Overall score {data?.lowRatings?.threshold ?? 2} or below, given in the period, newest first.
        </p>
        <DataTable
          columns={[
            { key: 'createdAt', label: 'Rated', render: (r: any) => formatDateTime(r.createdAt) },
            { key: 'driver', label: 'Driver', value: (r: any) => r.driver.fullName, render: (r: any) => <Person person={r.driver} /> },
            { key: 'passenger', label: 'Rated by', value: (r: any) => r.passenger.fullName, render: (r: any) => <Person person={r.passenger} /> },
            { key: 'overall', label: 'Overall', numeric: true },
            { key: 'safety', label: 'Safety', numeric: true },
            { key: 'accuracy', label: 'Accuracy', numeric: true },
            { key: 'comment', label: 'Comment', render: (r: any) => <span className="stats-comment">{r.comment || '—'}</span> },
          ]}
          rows={data?.lowRatings?.items || []}
          exportName="zippel-low-ratings"
          rowKey={(r: any) => r.id}
          empty="No low ratings in this period."
        />
      </section>

      <div className="stats-columns">
        <section className="stats-section">
          <h2>Blocked by more than one member</h2>
          <p className="stats-section-intro">As things stand now, whatever the period.</p>
          <DataTable
            columns={[
              { key: 'fullName', label: 'Member', render: (r: any) => <Person person={r} /> },
              { key: 'blockers', label: 'Blocked by', numeric: true },
              { key: 'lastBlockedAt', label: 'Last blocked', render: (r: any) => formatDateTime(r.lastBlockedAt) },
            ]}
            rows={data?.multiplyBlocked || []}
            exportName="zippel-multiply-blocked"
            rowKey={(r: any) => r.userId}
            empty="Nobody is blocked by more than one member."
          />
        </section>

        <section className="stats-section">
          <h2>Support inbox</h2>
          <InboxSummary />
        </section>
      </div>

      <section className="stats-section">
        <h2>Drivers cancelling unusually often</h2>
        <p className="stats-section-intro">
          Rides departing in the period. Listed when a driver with at least {cancellations?.minRides ?? 3} rides
          cancelled more than {cancellations?.multiple ?? 2}× the platform rate ({formatShare(cancellations?.platformRate)}).
        </p>
        <DataTable
          columns={[
            { key: 'fullName', label: 'Driver', render: (r: any) => <Person person={r} /> },
            { key: 'rides', label: 'Rides', numeric: true },
            { key: 'cancelled', label: 'Cancelled', numeric: true },
            { key: 'rate', label: 'Rate', numeric: true, render: (r: any) => formatShare(r.rate) },
            { key: 'passengersRemoved', label: 'Passengers removed', numeric: true },
          ]}
          rows={cancellations?.drivers || []}
          exportName="zippel-driver-cancellations"
          rowKey={(r: any) => r.userId}
          empty="No driver stands out in this period."
        />
        <p className="stats-note">No-shows are not listed: nothing in the app records one yet.</p>
      </section>
    </StatisticsPage>
  );
};

export default TrustSafety;
